import React from 'react'

import Button from '../button'
import Input from '../input'
import styles from '../../styles/modules/modal/modal.module.scss'
import { strings } from './modal-content'
import { Config } from '../../../core/toast'
import {
  SupportedLanguageCode,
  LANGUAGE_CODES,
  StationTranslation,
} from '../../../core/i18n'
import { BrandCode } from '../common'
import { roleType } from '../../../core/auth0'
import { Brand, DEFAULT_BRAND_CODE } from '../../../core/brands'

interface Props {
  isLoading: boolean
  role: roleType
  brands: Brand[]
  createStation: (
    name: string,
    extra: string,
    fullAddress: string,
    openingHours: string,
    latitude: number,
    longitude: number,
    stationTranslations?: Array<{
      langCode: string
      name?: string
      extra?: string
      fullAddress?: string
    }>,
    brandCode?: string
  ) => void
  show: (config: Config) => void
}

interface State {
  readonly name: string
  readonly extra: string
  readonly fullAddress: string
  readonly openingHours: string
  readonly latitude: string
  readonly longitude: string
  readonly selectedLanguageCode: SupportedLanguageCode
  readonly translations: StationTranslation[]
  readonly selectedBrand: BrandCode
}

export class CreateStationForm extends React.PureComponent<Props, State> {
  readonly state = {
    name: '',
    extra: '',
    fullAddress: '',
    openingHours: '',
    latitude: '',
    longitude: '',
    selectedLanguageCode: LANGUAGE_CODES.JAPANESE as SupportedLanguageCode,
    translations: [
      {
        langCode: LANGUAGE_CODES.JAPANESE,
        name: '',
        extra: '',
        fullAddress: '',
      },
      {
        langCode: LANGUAGE_CODES.ENGLISH,
        name: '',
        extra: '',
        fullAddress: '',
      },
    ],
    selectedBrand: (DEFAULT_BRAND_CODE as BrandCode),
  }

  componentDidMount() {
    // デフォルトブランドを最初のブランドに設定
    if (this.props.brands.length > 0) {
      this.setState({ selectedBrand: this.props.brands[0].code })
    }
  }

  handleLanguageChange = (languageCode: SupportedLanguageCode) => {
    this.setState({ selectedLanguageCode: languageCode })
  }

  handleBrandChange = (brand: BrandCode) => {
    this.setState({ selectedBrand: brand })
  }

  handleTranslationChange = (
    field: 'name' | 'extra' | 'fullAddress',
    value: string
  ) => {
    const { selectedLanguageCode, translations } = this.state
    const updatedTranslations = translations.map(t =>
      t.langCode === selectedLanguageCode ? { ...t, [field]: value } : t
    )
    this.setState({ translations: updatedTranslations })
  }

  getCurrentTranslation = () => {
    return (
      this.state.translations.find(
        t => t.langCode === this.state.selectedLanguageCode
      ) || {
        langCode: this.state.selectedLanguageCode,
        name: '',
        extra: '',
        fullAddress: '',
      }
    )
  }

  handleOnClickSubmit = () => {
    const latitude = parseFloat(this.state.latitude)
    const longitude = parseFloat(this.state.longitude)
    const {
      name,
      extra,
      fullAddress,
      openingHours,
      translations,
      selectedBrand,
    } = this.state
    const isAdmin = this.props.role === roleType.ADMIN

    if (
      !name ||
      !extra ||
      !fullAddress ||
      !openingHours ||
      isNaN(latitude) ||
      isNaN(longitude)
    ) {
      const config: Config = {
        type: 'error',
        title: strings.toastError.title,
        body: strings.toastError.body,
      }
      this.props.show(config)
      return
    }

    // 日本語以外の翻訳のみを抽出
    const nonJapaneseTranslations = translations.filter(
      t => t.langCode !== LANGUAGE_CODES.JAPANESE
    )

    this.props.createStation(
      name,
      extra,
      fullAddress,
      openingHours,
      latitude,
      longitude,
      nonJapaneseTranslations.length > 0 ? nonJapaneseTranslations : undefined,
      isAdmin ? selectedBrand : undefined
    )
  }

  render() {
    const { isLoading, role, brands } = this.props
    const { selectedLanguageCode, selectedBrand } = this.state
    const currentTranslation = this.getCurrentTranslation()
    const isAdmin = role === roleType.ADMIN

    return (
      <main className={styles.main}>
        {/* 言語選択 */}
        <div className={styles.languageSelection}>
          <h3 className={styles.languageLabel}>言語選択</h3>
          <select
            className={styles.languageSelect}
            value={selectedLanguageCode}
            onChange={event =>
              this.handleLanguageChange(
                event.target.value as SupportedLanguageCode
              )
            }
          >
            <option value={LANGUAGE_CODES.JAPANESE}>日本語</option>
            <option value={LANGUAGE_CODES.ENGLISH}>英語</option>
          </select>
        </div>

        {/* ブランド選択（管理者のみ） */}
        {isAdmin && (
          <div className={styles.languageSelection}>
            <h3 className={styles.languageLabel}>ブランド選択</h3>
            <select
              className={styles.languageSelect}
              value={selectedBrand || ''}
              onChange={event =>
                this.handleBrandChange(event.target.value as BrandCode)
              }
            >
              {brands.map(brand => (
                <option key={brand.code} value={brand.code}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* 多言語情報 */}
        <Input
          label={strings.station.name}
          type={'text'}
          onChange={event => {
            if (selectedLanguageCode === LANGUAGE_CODES.JAPANESE) {
              this.setState({ name: event.target.value })
            }
            this.handleTranslationChange('name', event.target.value)
          }}
          value={
            selectedLanguageCode === LANGUAGE_CODES.JAPANESE
              ? this.state.name
              : currentTranslation.name || ''
          }
        />
        <Input
          label={strings.station.extra}
          type={'text'}
          onChange={event => {
            if (selectedLanguageCode === LANGUAGE_CODES.JAPANESE) {
              this.setState({ extra: event.target.value })
            }
            this.handleTranslationChange('extra', event.target.value)
          }}
          value={
            selectedLanguageCode === LANGUAGE_CODES.JAPANESE
              ? this.state.extra
              : currentTranslation.extra || ''
          }
        />
        <Input
          label={strings.station.fullAddress}
          type={'text'}
          onChange={event => {
            if (selectedLanguageCode === LANGUAGE_CODES.JAPANESE) {
              this.setState({ fullAddress: event.target.value })
            }
            this.handleTranslationChange('fullAddress', event.target.value)
          }}
          value={
            selectedLanguageCode === LANGUAGE_CODES.JAPANESE
              ? this.state.fullAddress
              : currentTranslation.fullAddress || ''
          }
        />

        {/* 基本情報 */}
        <Input
          label={strings.station.openingHours}
          type={'text'}
          onChange={event => {
            this.setState({ openingHours: event.target.value })
          }}
          value={this.state.openingHours}
          disabled={selectedLanguageCode === LANGUAGE_CODES.ENGLISH}
        />
        <Input
          label={strings.station.latitude}
          type={'text'}
          onChange={event => {
            this.setState({ latitude: event.target.value })
          }}
          value={this.state.latitude}
          disabled={selectedLanguageCode === LANGUAGE_CODES.ENGLISH}
        />
        <Input
          label={strings.station.longitude}
          type={'text'}
          onChange={event => {
            this.setState({ longitude: event.target.value })
          }}
          value={this.state.longitude}
          disabled={selectedLanguageCode === LANGUAGE_CODES.ENGLISH}
        />

        <Button
          title={strings.soracomSIM.createButton}
          onClick={this.handleOnClickSubmit}
          className={styles.report}
          isLoading={isLoading}
          loadingClassName={styles.loadingReport}
        />
      </main>
    )
  }
}
